// A night at Tsukinoura: the date picks the tide, the pools and what the sea
// left in them. Everything here is deterministic from the date string.
import { ONE, isqrt, idiv } from './num.js';
import { rand, hashText } from './rand.js';
import { tideName, lunarDay, phaseIndex, illuminationPermille } from './moon.js';
import { BASINS } from './basins.js';
import { TIDES, POOLS_PER_NIGHT, CRABS, LITTLE_KINDS, TUNE, MODE_TUNE } from './content.js';
import { makePool } from './pool.js';
import { pickRules } from './rules.js';
import { DEFAULT_SHELLS } from './shells.js';
import { MOTIFS } from './motifs.js';
import { gridFrame, CELL as GCELL } from './grid.js';

// The game of the night, from the real moon: stars on dark nights, pictures
// at full moon, lanterns when the tide turns, drift on quiet neap nights.
const MODE_BY_DAY = [null,
  'stars', 'stars', 'stars', 'pen', 'pen', 'pen', 'floats', 'floats', 'floats', 'lantern',
  'lantern', 'pen', 'pen', 'picture', 'picture', 'picture', 'pen', 'pen', 'lantern', 'lantern',
  'lantern', 'floats', 'floats', 'floats', 'picture', 'picture', 'pen', 'stars', 'stars', 'stars'];
export const MODES = ['pen', 'stars', 'lantern', 'floats', 'picture'];
// Names on the lanterns floated out on lantern nights (the player's offered
// lantern goes first; the rest are a prototype sample of other players).
export const SAMPLE_LANTERN_NAMES = ['はる', 'Mina', 'こうた', 'Sol', 'あおい', 'Ren', 'ゆき', 'Leo', 'さくら', 'Noa', 'たつ', 'Ivy'];
import { insidePoly } from './grid.js';

const u = v => Math.round(v * ONE);
export const basinPoly = b => b.outline.map(([x, y]) => [x * 4, y * 4]); // basins.js is 1024/unit

// Nights whose game is set by hand instead of by the moon (live-ops schedule:
// events, playtests). Same for every player, like the rest of the night.
export const SCHEDULE = {
  '2026-10-04': 'pen', '2026-10-05': 'pen',   // playtest of 灯の囲い
};

export function nightInfo(iso) {
  const tide = tideName(iso);
  const seed = hashText('tsukinoura:' + iso);
  const ld = lunarDay(iso);
  const motifs = Object.keys(MOTIFS);
  const motif = ld >= 14 && ld <= 16 ? 'rabbit' : motifs[(seed >>> 3) % motifs.length];
  return { date: iso, tide, lunarDay: ld, phase: phaseIndex(iso), light: illuminationPermille(iso), rules: TIDES[tide], seed, okite: pickRules(seed, tide), mode: SCHEDULE[iso] || MODE_BY_DAY[ld], motif };
}

// Pool plan for the night: three ordinary basins in shelf order, then the
// fuchi on big-tide nights (or one more ordinary basin otherwise).
// `shells` (optional) puts the player's own shells on the crabs (磯めぐり);
// tonight's tide always uses each crab's own shell.
export function nightPlan(iso, { shells = null, okite = null, ward = false, mode = null, lanterns = [] } = {}) {
  const info = { ...nightInfo(iso), shells, ward, lanternNames: [...lanterns, ...SAMPLE_LANTERN_NAMES] };
  if (okite) info.okite = okite;   // tests and the balance sweep force a rule set
  if (mode) info.mode = mode;
  const r = rand(info.seed);
  const ordinary = BASINS.filter(b => b.id !== 'fuchi');
  const count = info.rules.boss ? POOLS_PER_NIGHT : POOLS_PER_NIGHT + 1;
  const chosen = r.shuffle([...ordinary]).slice(0, count).sort((a, b) => a.centre[0] - b.centre[0]);
  if (info.rules.boss) chosen.push(BASINS.find(b => b.id === 'fuchi'));
  return { ...info, basins: chosen.map(b => b.id) };
}

// Distance from a point to the polygon edge (fixed point, integer only so
// every engine builds the same night).
function rimDistance(poly, x, y) {
  let best = Infinity;
  for (let i = 0; i < poly.length; i++) {
    const [ax, ay] = poly[i], [bx, by] = poly[(i + 1) % poly.length];
    const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    const t = Math.max(0, Math.min(l2, (x - ax) * dx + (y - ay) * dy));
    const qx = ax + idiv(dx * t, l2), qy = ay + idiv(dy * t, l2);
    best = Math.min(best, isqrt((x - qx) ** 2 + (y - qy) ** 2));
  }
  return best;
}
const far = (ax, ay, bx, by, d) => (ax - bx) ** 2 + (ay - by) ** 2 >= d * d;

// Build pool `index` of the night. `attempt` reshuffles contents (used when
// the verifier rejects a layout).
export function buildPool(plan, index, attempt = 0) {
  const basin = BASINS.find(b => b.id === plan.basins[index]);
  const poly = basinPoly(basin);
  const rules = plan.rules;
  const r = rand((plan.seed ^ Math.imul(index + 1, 0x9e3779b1) ^ Math.imul(attempt, 0x85ebca6b)) >>> 0);
  const isFuchi = basin.id === 'fuchi';
  const depth = index; // later pools get busier
  const taken = [];
  const free = (x, y, rad, rimGap) => {
    if (!insidePoly(poly, x, y) || rimDistance(poly, x, y) < rad + rimGap) return false;
    return taken.every(o => far(o.x, o.y, x, y, o.r + rad + u(0.35)));
  };
  // Bounding box of the outline for sampling.
  const xs = poly.map(p => p[0]), ys = poly.map(p => p[1]);
  const box = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const place = (rad, rimGap, test = () => true, tries = 400) => {
    for (let k = 0; k < tries; k++) {
      const x = box[0] + r.below(box[1] - box[0]), y = box[2] + r.below(box[3] - box[2]);
      if (free(x, y, rad, rimGap) && test(x, y)) { const o = { x, y, r: rad }; taken.push(o); return o; }
    }
    return null;
  };

  // Crabs enter from the shore side (bottom of the pool, toward the camera).
  const bottom = box[3];
  const crabs = [];
  for (let k = 0; k < 4; k++) {
    // Off the rim, so the very first shot is not automatically a rim-to-rim cut.
    // Spaced so a shot rarely hits a neighbour on its first frames.
    const spaced = (x, y) => crabs.every(c => far(c.x, c.y, x, y, u(1.9)));
    const o = place(TUNE.crabR, u(1.2), (x, y) => y > bottom - u(4.6) && Math.abs(x) < u(4.4) && spaced(x, y))
      || place(TUNE.crabR, u(0.6), (x, y) => y > bottom - u(5.5) && spaced(x, y))
      || place(TUNE.crabR, u(0.4));
    crabs.push({ char: CRABS[k].id, shell: plan.shells?.[CRABS[k].id] || DEFAULT_SHELLS[CRABS[k].id], x: o.x, y: o.y });
  }
  // Boss sits in the middle of the fuchi.
  let boss = null;
  if (isFuchi) {
    boss = { x: 0, y: -u(0.8), r: u(1.7) };
    taken.push(boss);
  }
  // Seeps: rim vertices spread round the pool, so a single cut rarely lights
  // the middle; that takes crossing wakes. Keep them off the crabs' entrance.
  const mt = MODE_TUNE[isFuchi ? 'pen' : plan.mode || 'pen'];
  const seepCount = isFuchi ? 2 : Math.max(1, Math.min(basin.seepVertices.length, rules.seeps + (depth >= 2 ? 1 : 0) + mt.seeps));
  const seepChoices = r.shuffle(basin.seepVertices.filter(v => crabs.every(c => far(poly[v][0], poly[v][1], c.x, c.y, u(2.6)))));
  const seeps = spreadPick(seepChoices.map(v => poly[v]), seepCount);
  for (const [x, y] of seeps) taken.push({ x, y, r: u(1.6) });

  const rocks = [], anemones = [], kelps = [];
  const rockCount = r.between(1, 2) + (depth >= 1 ? 1 : 0);
  for (let k = 0; k < rockCount; k++) { const o = place(u(0.55 + r.below(5) * 0.1), u(0.9)); if (o) rocks.push(o); }
  if (depth >= 1 || isFuchi) for (let k = 0; k < (depth >= 2 ? 2 : 1); k++) { const o = place(u(0.5), u(1.2)); if (o) anemones.push(o); }
  const law = plan.okite.rules;
  const kelpCount = law.includes('kelp') ? 3 : depth >= 2 ? 1 : 0;
  for (let k = 0; k < kelpCount; k++) {
    const x = box[0] + r.below(box[1] - box[0]), y = box[2] + r.below(box[3] - box[2]);
    if (insidePoly(poly, x, y)) kelps.push({ x, y, r: u(1.3) });
  }

  // What the game of the night puts in the pool (the fuchi is always the old one's pen).
  const mode = isFuchi ? 'pen' : plan.mode || 'pen';
  const littles = [];
  let exit = null, stencil = null;
  const nets = [];
  const awayFromSeeps = d => (x, y) => seeps.every(([sx, sy]) => far(sx, sy, x, y, d));
  if (mode === 'pen') {
    const nLittles = r.between(rules.littles[0], rules.littles[1]) + (isFuchi ? 1 : 0);
    for (let k = 0; k < nLittles; k++) {
      // Keep little ones away from the seeps at the start: the first turn is for reading the pool.
      const o = place(TUNE.littleR, u(0.4), (x, y) => awayFromSeeps(u(3.4))(x, y)
        && (!boss || far(boss.x, boss.y, x, y, boss.r + TUNE.bossInk + u(0.9))));
      if (o) littles.push({ kind: LITTLE_KINDS[r.below(LITTLE_KINDS.length)], x: o.x, y: o.y });
    }
  } else if (mode === 'stars') {
    // Reflections of the stars; clouds (the dark) creep over the ones left unlit.
    const n = 7 + Math.min(2, depth);
    for (let k = 0; k < n; k++) { const o = place(u(0.9), u(0.5), awayFromSeeps(u(2))); if (o) littles.push({ kind: 'star', x: o.x, y: o.y }); }
    // (Stars in rock pockets that start lit would be lost at once; makePool resolves them.)
  } else if (mode === 'lantern') {
    // The outlet to the sea on the far rim; the lanterns start on the shore side.
    const top = poly.reduce((a, b) => (b[1] < a[1] ? b : a));
    exit = { x: top[0], y: top[1], r: TUNE.exitR };
    taken.push({ x: exit.x, y: exit.y, r: u(2.5) });
    const n = 3 + (depth >= 1 ? 1 : 0);
    for (let k = 0; k < n; k++) {
      const o = place(TUNE.littleR, u(0.5), (x, y) => y > box[2] + (box[3] - box[2]) * 0.45 && awayFromSeeps(u(3))(x, y));
      if (o) littles.push({ kind: 'lantern', x: o.x, y: o.y, name: plan.lanternNames[(index * 4 + k) % plan.lanternNames.length] });
    }
  } else if (mode === 'floats') {
    // Nets at rim vertices spread round the pool; glass floats inside.
    const cand = r.shuffle(poly.filter(v => seeps.every(([sx, sy]) => far(sx, sy, v[0], v[1], u(3))) && crabs.every(c => far(c.x, c.y, v[0], v[1], u(2.5)))));
    for (const [x, y] of spreadPick(cand, 3)) { nets.push({ x, y, r: TUNE.netR }); taken.push({ x, y, r: u(1.4) }); }
    for (let k = 0; k < 5; k++) { const o = place(TUNE.floatR, u(0.9)); if (o) littles.push({ kind: 'float', x: o.x, y: o.y }); }
  } else if (mode === 'picture') {
    // The night's picture, scaled into the pool, as the cells to light.
    const bmp = MOTIFS[plan.motif];
    const fr = gridFrame(poly);
    const bw = box[1] - box[0], bh = box[3] - box[2];
    const size = Math.min(bw, bh) * 0.86;
    const ox = box[0] + (bw - size) / 2, oy = box[2] + (bh - size) / 2;
    const cells = [];
    for (let cy = 0; cy < fr.h; cy++) for (let cx = 0; cx < fr.w; cx++) {
      const px = fr.x0 + cx * GCELL + GCELL / 2, py = fr.y0 + cy * GCELL + GCELL / 2;
      const mx = Math.floor(((px - ox) * 16) / size), my = Math.floor(((py - oy) * 16) / size);
      if (mx < 0 || my < 0 || mx > 15 || my > 15 || bmp[my][mx] !== '#') continue;
      if (insidePoly(poly, px, py)) cells.push(cx + cy * fr.w);
    }
    stencil = { cells, goal: TUNE.stencilGoal };
  }

  // Things tonight's rules put in the pool.
  const vortices = [], logs = [], pearls = [];
  let rival = null;
  if (law.includes('vortex')) {
    const a = place(TUNE.vortexR, u(0.6));
    const b = a && place(TUNE.vortexR, u(0.6), (x, y) => far(a.x, a.y, x, y, u(5.5)));
    if (a && b) vortices.push({ a: { x: a.x, y: a.y }, b: { x: b.x, y: b.y }, r: TUNE.vortexR });
  }
  if (law.includes('driftwood')) for (let k = 0; k < 2; k++) { const o = place(TUNE.logR, u(0.8)); if (o) logs.push({ x: o.x, y: o.y, r: TUNE.logR }); }
  if (law.includes('rival')) { const o = place(TUNE.rivalR, u(1.0), (x, y) => y < bottom - u(4)); if (o) rival = { x: o.x, y: o.y, r: TUNE.rivalR }; }
  if (law.includes('pearls')) for (let k = 0; k < 5; k++) { const o = place(TUNE.pearlR, u(0.5)); if (o) pearls.push({ x: o.x, y: o.y }); }
  const poolRules = {
    current: law.includes('current') ? plan.okite.params.currentDir : null,
    rain: law.includes('rain'), fog: law.includes('fog'), calm: law.includes('calm'), swift: law.includes('swift'),
    bloom: law.includes('bloom') ? TUNE.bloomWake : 1000,
    ward: !!plan.ward,
  };

  return makePool({
    basin: basin.id, poly, crabs, littles, rocks, anemones, kelps, seeps, boss,
    spread: Math.max(1, rules.spread + mt.spread), seepRadius: u(1.1),
    rules: poolRules, vortices, logs, rival, pearls,
    mode, exit, nets, stencil,
  });
}

// Pick n points greedily maximising the smallest gap between them.
function spreadPick(points, n) {
  if (points.length <= n) return points;
  const out = [points[0]];
  while (out.length < n) {
    let best = null, bestD = -1;
    for (const p of points) {
      if (out.includes(p)) continue;
      const d = Math.min(...out.map(q => (p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2));
      if (d > bestD) { bestD = d; best = p; }
    }
    out.push(best);
  }
  return out;
}

export const dist = (a, b) => isqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
